import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { Observable } from 'rxjs';
import { Config } from '../app.configs';
import { CacheService } from './cache.service';

export interface WalletBalance {
  amount: number;
}

export interface PrivyWalletBalance {
  USDC: number;
  EURC: number;
}

@Injectable({
  providedIn: 'root',
})
export class AdminService {
  private readonly CACHE_PREFIX = 'admin-service:';

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private cacheService: CacheService,
  ) { }

  getWalletBalance(
    address: string,
    forceRefresh: boolean = false,
  ): Observable<WalletBalance> {
    const cacheKey = `${this.CACHE_PREFIX}wallet-balance:${address}`;

    return this.cacheService.get<WalletBalance>(
      cacheKey,
      () => {
        const headers = this.authService.getHeaders();
        return this.http.get<WalletBalance>(
          Config.toApiUrl(`dashboard/admin/wallet-balances/${address}/`),
          { headers, withCredentials: true },
        );
      },
      undefined,
      forceRefresh,
    );
  }

  /** Fetch USDC and EURC balances for a Privy wallet, by its Privy wallet ID. */
  getPrivyWalletBalance(
    walletId: string,
    forceRefresh: boolean = false,
  ): Observable<PrivyWalletBalance> {
    const cacheKey = `${this.CACHE_PREFIX}privy-wallet-balance:${walletId}`;

    return this.cacheService.get<PrivyWalletBalance>(
      cacheKey,
      () => {
        const headers = this.authService.getHeaders();
        return this.http.get<PrivyWalletBalance>(
          Config.toApiUrl(`dashboard/admin/privy-wallet-balances/${walletId}/`),
          { headers, withCredentials: true },
        );
      },
      undefined,
      forceRefresh,
    );
  }

  setProviderRate(
    provider: string,
    rate: number,
    active: boolean,
  ): Observable<void> {
    const headers = this.authService.getHeaders();
    return this.http.patch<void>(
      Config.toApiUrl('dashboard/admin/set-provider-rate/'),
      { provider, rate, active },
      { headers, withCredentials: true },
    );
  }

  createMomoDeposit(
    amount: string,
    phoneNumber: string,
    fullname: string,
    reason?: string,
  ): Observable<any> {
    const headers = this.authService.getHeaders();
    return this.http.post<any>(
      Config.toApiUrl('dashboard/admin/create-momo-deposit/'),
      { amount, phoneNumber, fullname, reason },
      { headers, withCredentials: true },
    );
  }

  createInternalTransfer(payload: {
    source_provider: 'PRIVY' | 'BRIDGE';
    source_wallet: 'YIELD' | 'SEPA' | 'KKIAPAY' | 'REVOLUPAY' | 'YELLOWCARD' | 'MARKETING';
    destination_provider: 'PRIVY' | 'BRIDGE';
    destination_wallet: 'YIELD' | 'SEPA' | 'KKIAPAY' | 'REVOLUPAY' | 'YELLOWCARD' | 'MARKETING';
    destination_address?: string | null;
    amount: number;
  }): Observable<any> {
    const headers = this.authService.getHeaders();
    return this.http.post<any>(
      Config.toApiUrl('dashboard/admin/create-internal-transfer/'),
      payload,
      { headers, withCredentials: true },
    );
  }

  invalidateCache(): void {
    this.cacheService.invalidateByPrefix(this.CACHE_PREFIX);
  }
}
